import {
	createContext,
	FC,
	PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

import { PassengerBookingView } from '@mopla/data-models';

interface IMoreActionsContext {
	isMoreActionsOpen: boolean;
	openMoreActions: (booking: PassengerBookingView) => void;
	closeMoreActions: () => void;
	currentBooking: PassengerBookingView | null;
}

const MoreActionsContext = createContext<IMoreActionsContext | undefined>(
	undefined
);

export const useMoreActionsContext = () => {
	const context = useContext(MoreActionsContext);
	if (!context) {
		throw new Error(
			'useMoreActionsContext must be used within a MoreActionsProvider'
		);
	}
	return context;
};

export const MoreActionsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [isMoreActionsOpen, setIsMoreActionsOpen] = useState<boolean>(false);
	const [currentBooking, setCurrentBooking] =
		useState<PassengerBookingView | null>(null);

	const openMoreActions = useCallback((booking: PassengerBookingView) => {
		setCurrentBooking(booking);
		setIsMoreActionsOpen(true);
	}, []);

	const closeMoreActions = useCallback(() => {
		setCurrentBooking(null);
		setIsMoreActionsOpen(false);
	}, []);

	const value: IMoreActionsContext = useMemo(
		() => ({
			isMoreActionsOpen,
			openMoreActions,
			closeMoreActions,
			currentBooking,
		}),
		[closeMoreActions, currentBooking, isMoreActionsOpen, openMoreActions]
	);

	return (
		<MoreActionsContext.Provider value={value}>
			{children}
		</MoreActionsContext.Provider>
	);
};
